import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { cleanV2FilterPayload } from '../../../core/service/base/filter-cleaner';
import { LocalDate } from '../../../core/model/local-date';
import { Money } from '../../../core/model/money';
import { DataService } from '../../../core/service/base/data.service';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { AmortizationPlanPaymentNoticeListItem } from '../../model/amortization-plan-payment-notice-list-item';
import { ReminderDeliveryMethod } from '../../model/const/enum';

@Injectable()
export class AmortizationPlanPaymentNoticeService extends DataService<AmortizationPlanPaymentNoticeListItem> {
  private readonly _defaultFilterFields: string[] = ['paymentStatus', 'deliveryStatus', 'dueFrom', 'dueTo'];

  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, AmortizationPlanPaymentNoticeListItem, progressBar, 'amortization-plan-payment-notice');
  }

  protected createModel(data: any): AmortizationPlanPaymentNoticeListItem {
    const model = super.createModel(data);
    model.dueDate = LocalDate.fromDate(data?.dueDate);
    model.amount = this.mapMoney(data?.amount);
    model.remainingAmount = this.mapMoney(data?.remainingAmount ?? data?.amount);
    model.createdDate = data?.createdDate ? new Date(data.createdDate) : null;
    model.sentDate = data?.sentDate ? new Date(data.sentDate) : null;
    model.latestDeliveryCreatedDate = data?.latestDeliveryCreatedDate ? new Date(data.latestDeliveryCreatedDate) : null;
    model.latestDeliveryLastAttemptedAt = data?.latestDeliveryLastAttemptedAt ? new Date(data.latestDeliveryLastAttemptedAt) : null;
    return model;
  }

  override prepareFilter(filter: any): any {
    return cleanV2FilterPayload(super.prepareFilter(filter));
  }

  mapFilterValue(key: string, value: any): any {
    const mappedValue = super.mapFilterValue(key, value);
    switch (key) {
      case 'companyId':
      case 'deliveryMethod':
        return mappedValue === '' ? null : mappedValue;
      case 'paymentStatus':
      case 'deliveryStatus':
        return mappedValue === '' || mappedValue === null || mappedValue === undefined ? 0 : Number(mappedValue);
      case 'amortizationPlanNumber':
        return mappedValue === '' || mappedValue === null || mappedValue === undefined ? null : Number(mappedValue);
      default:
        return mappedValue;
    }
  }

  getFilterDefinition(): Observable<{
    pageSize: number;
    filter: {
      companyId: string;
      amortizationPlanNumber: number;
      ocrNumber: string;
      deliveryMethod: ReminderDeliveryMethod;
      paymentStatus: number;
      deliveryStatus: number;
      dueFrom: LocalDate;
      dueTo: LocalDate;
      createdFrom: LocalDate;
      createdTo: LocalDate;
      selectedFields: string[];
      defaultFields: string[];
    };
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(response => {
        const filter = response?.filter ?? {};
        return {
          pageSize: response?.pageSize,
          filter: {
            companyId: filter.companyId ?? '',
            amortizationPlanNumber: typeof filter.amortizationPlanNumber === 'number' ? filter.amortizationPlanNumber : null,
            ocrNumber: filter.ocrNumber ?? '',
            deliveryMethod: typeof filter.deliveryMethod === 'number' ? filter.deliveryMethod : null,
            paymentStatus: typeof filter.paymentStatus === 'number' ? filter.paymentStatus : 0,
            deliveryStatus: typeof filter.deliveryStatus === 'number' ? filter.deliveryStatus : 0,
            dueFrom: LocalDate.fromDate(filter.dueFrom),
            dueTo: LocalDate.fromDate(filter.dueTo),
            createdFrom: LocalDate.fromDate(filter.createdFrom),
            createdTo: LocalDate.fromDate(filter.createdTo),
            selectedFields: this.normalizeSelectedFields(filter.selectedFields),
            defaultFields: this._defaultFilterFields,
          },
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  requestDeliveryFiltered(filter: any, includedIds: string[], excludedIds: string[], affectedItemsCount: number): Observable<{ requestedCount: number }> {
    this.progressBar.start();
    return this.client
      .post<{ requestedCount: number }>(
        `${this.endpoint}/delivery`,
        JSON.stringify({
          filter: this.prepareFilter({ ...(filter ?? {}) }),
          includedIds: includedIds ?? [],
          excludedIds: excludedIds ?? [],
          affectedItemsCount,
        })
      )
      .pipe(
        tap(() => this.notification.showSuccess('AMORTIZATION_PLAN.PAYMENT_NOTICE.DELIVERY_REQUEST_SUCCESS')),
        finalize(() => this.progressBar.stop())
      );
  }

  private mapMoney(response: any): Money {
    return new Money(response?.currencyCode ?? response?.currency?.isoCode ?? '', response?.amount ?? 0);
  }

  private normalizeSelectedFields(selectedFields: any): string[] {
    if (!Array.isArray(selectedFields)) {
      return this._defaultFilterFields;
    }

    return selectedFields.map(field => {
      switch (field) {
        case 'paymentstatus':
          return 'paymentStatus';
        case 'deliverystatus':
          return 'deliveryStatus';
        case 'duefrom':
          return 'dueFrom';
        case 'dueto':
          return 'dueTo';
        default:
          return field;
      }
    });
  }
}
